import { apiClient } from '@/lib/apiClient';
import type { PlatformRole } from '@/lib/abilities';
import { isFeatureDisabledError } from '@/lib/userFacingError';
import { isPlatformFeatureEnabled, useAuthStore, type PlatformFeatureName, type PlatformFeatures } from '@/store/authStore';

export interface LoginResponse {
    accessToken: string;
    user: {
        id: string;
        email: string;
        firstName: string | null;
        lastName: string | null;
        role: PlatformRole;
    };
    sessionId: string;
    /** BE-07: signed in with a temporary password; route to /change-password. */
    mustChangePassword?: boolean;
}

export async function login(
    email: string,
    password: string,
    rememberMe = false,
    mfaCode?: string,
): Promise<LoginResponse> {
    const { data } = await apiClient.post<{ data: LoginResponse }>('/auth/login', {
        email,
        password,
        rememberMe,
        // Only sent on the second submit, after the backend responds
        // PLATFORM_MFA_REQUIRED and the operator enters their TOTP code.
        ...(mfaCode ? { mfaCode } : {}),
    });
    return data.data;
}

/** True when a login attempt was rejected because a TOTP second factor is
 *  required — the caller should reveal the MFA code field and resubmit
 *  /auth/login with the same credentials plus `mfaCode`. */
export function isLoginMfaRequired(err: unknown): boolean {
    const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
    return code === 'PLATFORM_MFA_REQUIRED';
}

/**
 * Reads `features` from a GET /auth/me payload defensively: only boolean
 * `impersonation` / `chat` flags are kept; anything else (older backend without
 * the field, wrong types) yields null = unknown.
 */
export function parsePlatformFeatures(raw: unknown): PlatformFeatures | null {
    if (!raw || typeof raw !== 'object') return null;
    const src = raw as Record<string, unknown>;
    const out: PlatformFeatures = {};
    if (typeof src.impersonation === 'boolean') out.impersonation = src.impersonation;
    if (typeof src.chat === 'boolean') out.chat = src.chat;
    return out;
}

export async function me(): Promise<LoginResponse['user'] & { mfaEnabled: boolean; lastLoginAt: string | null; features?: unknown }> {
    const { data } = await apiClient.get('/auth/me');
    return data.data;
}

/** UX-12: always resolves with the same body whether or not the account exists. */
export async function forgotPassword(email: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { email });
}

/** UX-12: consume the emailed single-use token. 400 = invalid or expired link. */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/reset-password', { token, newPassword });
}

/** BE-07: returns a fresh access token without `pwc`; the refresh cookie is reset server-side. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<{ accessToken: string }> {
    const { data } = await apiClient.post<{ data: { accessToken: string } }>('/auth/change-password', {
        currentPassword,
        newPassword,
    });
    return data.data;
}

export async function logout(): Promise<void> {
    await apiClient.post('/auth/logout');
}

// ---------------------------------------------------------------------------
// MFA (TOTP) — self-service enroll + step-up for the signed-in operator.
// Backend: backend/src/modules/platform/auth/mfa/* (mounted at /auth/mfa).
// Sensitive surfaces (billing sync, impersonation) are gated by
// requireRecentMfa(300): the operator must have stepped up within 5 minutes.
// ---------------------------------------------------------------------------

export interface MfaStatus {
    enabled: boolean;
    verifiedAt: string | null;
}

export async function mfaStatus(): Promise<MfaStatus> {
    const { data } = await apiClient.get<{ data: MfaStatus }>('/auth/mfa/status');
    return data.data;
}

export async function mfaEnroll(): Promise<{ secret: string; otpauthUri: string }> {
    const { data } = await apiClient.post<{ data: { secret: string; otpauthUri: string } }>('/auth/mfa/enroll', {});
    return data.data;
}

export async function mfaVerifyEnroll(code: string): Promise<{ enabled: true }> {
    const { data } = await apiClient.post<{ data: { enabled: true } }>('/auth/mfa/verify-enroll', { code });
    return data.data;
}

export async function mfaStepUp(code: string): Promise<{ verifiedAt: string }> {
    const { data } = await apiClient.post<{ data: { verifiedAt: string } }>('/auth/mfa/step-up', { code });
    return data.data;
}

/** Disable/reset TOTP. Requires the account password + a current 6-digit code
 *  (backend disableMfaSchema). To re-key, disable then enroll again. */
export async function mfaDisable(password: string, code: string): Promise<{ disabled: true }> {
    const { data } = await apiClient.post<{ data: { disabled: true } }>('/auth/mfa/disable', { password, code });
    return data.data;
}

/** True when an error is the 401 emitted by requireRecentMfa — the caller
 *  should open the MFA step-up dialog and retry after verification. */
export function isMfaStepUpRequired(err: unknown): boolean {
    const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
    return code === 'PLATFORM_MFA_STEP_UP_REQUIRED' || code === 'PLATFORM_MFA_REQUIRED';
}

/**
 * Local stand-in for the backend's 503 PLATFORM_FEATURE_DISABLED, thrown without
 * a network call when /auth/me already said the feature is off. Same shape as an
 * AxiosError response so existing isFeatureDisabledError checks match.
 */
export function localFeatureDisabledError(feature: PlatformFeatureName): Error {
    return Object.assign(new Error(`Feature ${feature} is disabled`), {
        response: { status: 503, data: { error: { code: 'PLATFORM_FEATURE_DISABLED', message: `Feature ${feature} is disabled` } } },
    });
}

/**
 * Runs a poll for a switchable feature: skips the request when the feature is
 * known to be off, and records a PLATFORM_FEATURE_DISABLED answer as a silent
 * disable so later mounts do not poll again (production item 1 & 2).
 */
export async function pollFeature<T>(feature: PlatformFeatureName, run: () => Promise<T>): Promise<T> {
    if (!isPlatformFeatureEnabled(feature)) throw localFeatureDisabledError(feature);
    try {
        return await run();
    } catch (err) {
        if (isFeatureDisabledError(err)) useAuthStore.getState().disableFeature(feature);
        throw err;
    }
}
